import React from "react";
import { FEE_STRUCTURE } from "../../data/programs";
import { Wallet, Calculator } from "lucide-react";

export const FeeStructure = () => {
  return (
    <section
      id="fees"
      data-testid="fees-section"
      className="py-20 md:py-24 bg-aiou-paper/60 border-y border-aiou-sand/60"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.2em] text-aiou-gold font-semibold">
              Universal fee structure
            </span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-aiou-ink leading-[1.1] tracking-tight">
              Transparent. Same across all programs.
            </h2>
            <p className="mt-5 text-aiou-slate text-base sm:text-lg leading-relaxed">
              AIOU keeps fees simple for international students: one-time
              registration and admission charges, then a flat per-course fee.
              Your first-semester total only depends on how many courses your
              program runs in the first semester.
            </p>
            <p className="mt-4 text-xs text-aiou-mute leading-relaxed">
              Figures are from Spring-2026 prospectuses. Fee for subsequent
              semesters is communicated at the time of each admission.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-aiou-sand bg-white p-6 sm:p-8 shadow-[0_10px_30px_rgba(36,66,101,0.06)]">
              <div className="flex items-center gap-3 mb-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-aiou-steel/8 text-aiou-steel">
                  <Calculator className="h-5 w-5" strokeWidth={1.6} />
                </span>
                <h3 className="font-serif text-xl text-aiou-ink">
                  Fee breakdown (in USD)
                </h3>
              </div>

              <div className="divide-y divide-aiou-sand">
                <Row
                  label="Registration Fee"
                  note="One-time, at first admission"
                  value={`$${FEE_STRUCTURE.registration}`}
                />
                <Row
                  label="Admission Fee"
                  note="One-time, at first admission"
                  value={`$${FEE_STRUCTURE.admission}`}
                />
                <Row
                  label="Per-course Fee"
                  note="Per 3-credit-hour course"
                  value={`$${FEE_STRUCTURE.perCourse}`}
                />
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TotalCard
                  label="If 1st semester has 5 courses"
                  total={FEE_STRUCTURE.totalFiveCourses}
                  breakdown="$50 + $50 + ($100 × 5)"
                />
                <TotalCard
                  label="If 1st semester has 6 courses"
                  total={FEE_STRUCTURE.totalSixCourses}
                  breakdown="$50 + $50 + ($100 × 6)"
                  accent
                />
              </div>

              <div className="mt-6 flex items-start gap-3 rounded-xl bg-aiou-gold/10 border border-aiou-gold/30 p-4 text-sm text-aiou-ink leading-relaxed">
                <Wallet className="h-4 w-4 mt-0.5 shrink-0 text-aiou-goldDark" strokeWidth={1.8} />
                <span>
                  Fees can be paid in USD via bank draft, online card payment,
                  or AIOU's designated overseas bank accounts. Kamran can walk
                  you through the easiest method for your country.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Row = ({ label, note, value }) => (
  <div className="flex items-center justify-between py-3.5">
    <div>
      <div className="text-aiou-ink font-medium">{label}</div>
      <div className="text-xs text-aiou-mute mt-0.5">{note}</div>
    </div>
    <div className="font-serif text-2xl text-aiou-ink">{value}</div>
  </div>
);

const TotalCard = ({ label, total, breakdown, accent }) => (
  <div
    className={[
      "rounded-xl p-4 border",
      accent
        ? "bg-aiou-steel text-white border-aiou-steel"
        : "bg-aiou-paper border-aiou-sand text-aiou-ink",
    ].join(" ")}
  >
    <div
      className={[
        "text-[11px] uppercase tracking-wider",
        accent ? "text-white/70" : "text-aiou-mute",
      ].join(" ")}
    >
      {label}
    </div>
    <div className="font-serif text-3xl mt-1">US$ {total}</div>
    <div
      className={[
        "text-xs mt-1",
        accent ? "text-white/80" : "text-aiou-slate",
      ].join(" ")}
    >
      {breakdown}
    </div>
  </div>
);
